import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { io } from "socket.io-client";
import { requestResync } from "../utils/syncGuard";
import useAuthStore from "../store/authStore";

// Reload data when the tab comes back after being hidden at least this long
const RESYNC_AFTER_HIDDEN_MS = 30 * 1000;

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);
  // Rooms this client has joined, so they can be joined again after a reconnect
  const joinedRoomsRef = useRef(new Map());

  useEffect(() => {
    // Initialize socket connection
    const SOCKET_URL =
      import.meta.env.VITE_SOCKET_URL || "http://localhost:5001";

    const newSocket = io(SOCKET_URL, {
      transports: ["websocket"],
      // The login token identifies the user, so the server only lets this
      // socket into rooms (and events) the user has access to. Read on every
      // (re)connect, so it is always the current token.
      auth: (cb) => cb({ token: useAuthStore.getState().token }),
      reconnection: true,
      reconnectionDelay: 1000,
      // Keep trying forever - a tab that gives up silently stops receiving updates
      reconnectionAttempts: Infinity,
    });

    let hasConnectedBefore = false;

    newSocket.on("connect", () => {
      setIsConnected(true);

      if (hasConnectedBefore) {
        // Rooms are lost when the connection drops - join them again
        joinedRoomsRef.current.forEach(({ joinEvent, id }) => {
          newSocket.emit(joinEvent, id);
        });
        // Events sent while disconnected were missed - reload data
        requestResync("reconnect");
      }
      hasConnectedBefore = true;
    });

    newSocket.on("disconnect", (reason) => {
      setIsConnected(false);
    });

    newSocket.on("connect_error", (error) => {
      console.error("WebSocket connection error:", error);
      setIsConnected(false);
    });

    // A tab that was hidden (e.g. laptop asleep) may have missed updates
    let hiddenAt = null;
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        return;
      }

      if (!newSocket.connected) {
        // Reconnecting triggers a resync from the "connect" handler
        newSocket.connect();
      } else if (hiddenAt && Date.now() - hiddenAt >= RESYNC_AFTER_HIDDEN_MS) {
        requestResync("visible");
      }
      hiddenAt = null;
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    socketRef.current = newSocket;
    setSocket(newSocket);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (newSocket) {
        newSocket.disconnect();
      }
    };
  }, []);

  // Reconnect when the user logs in or out, so the server knows who this
  // socket belongs to (the "connect" handler then rejoins rooms and resyncs)
  const token = useAuthStore((state) => state.token);
  const lastTokenRef = useRef(token);
  useEffect(() => {
    if (!socket || lastTokenRef.current === token) return;
    lastTokenRef.current = token;
    if (!token) joinedRoomsRef.current.clear();
    socket.disconnect().connect();
  }, [socket, token]);

  const joinRoom = (joinEvent, id) => {
    if (socket && id) {
      joinedRoomsRef.current.set(`${joinEvent}:${id}`, { joinEvent, id });
      socket.emit(joinEvent, id);
    }
  };

  const leaveRoom = (joinEvent, leaveEvent, id) => {
    if (socket && id) {
      joinedRoomsRef.current.delete(`${joinEvent}:${id}`);
      socket.emit(leaveEvent, id);
    }
  };

  const joinOrganization = (organizationId) => {
    joinRoom("join-organization", organizationId);
  };

  const leaveOrganization = (organizationId) => {
    leaveRoom("join-organization", "leave-organization", organizationId);
  };

  const joinProject = (projectId) => {
    joinRoom("join-project", projectId);
  };

  const leaveProject = (projectId) => {
    leaveRoom("join-project", "leave-project", projectId);
  };

  const joinConversation = (conversationId) => {
    joinRoom("join-conversation", conversationId);
  };

  const leaveConversation = (conversationId) => {
    leaveRoom("join-conversation", "leave-conversation", conversationId);
  };

  const joinUser = (userId) => {
    joinRoom("join-user", userId);
  };

  const leaveUser = (userId) => {
    leaveRoom("join-user", "leave-user", userId);
  };

  const value = {
    socket,
    isConnected,
    joinOrganization,
    leaveOrganization,
    joinProject,
    leaveProject,
    joinConversation,
    leaveConversation,
    joinUser,
    leaveUser,
  };

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};
